import React, { Component } from 'react';
import Pin from '@appblocks/frontend/client/Components/Pin';
import PlatformUtils from '@appblocks/platforms/PlatformUtils';
import { SettingType, FeatureType } from '@appblocks/codegeneration/types';
import { DraggableCore } from 'react-draggable'; // <DraggableCore>
// import Draggable from 'react-draggable'; // <DraggableCore>
import { getSvgViewbox } from '../utils';

const isIE11 = !!window.MSInputMethodContext && !!document.documentMode;

let svgWidth = '80%';
let svgHeight = 'auto';
if (isIE11) {
    svgWidth = `${265 * 2}px`;
    svgHeight = `${310 * 2}px`;
}

const boardRef = React.createRef();

export default class Board extends Component {
    constructor(props) {
        super(props);
        this.clearHighlight = this.clearHighlight.bind(this);
        this.showPins = true;
        let pins = [];
        if (this.props.pins) {
            pins = this.props.pins;
        }
        this.state = {
            hoverPin: undefined,
            hoverPinText: '',
            search: '',
            pins,
            svg: '',
        };

        this.isActive = this.isActive.bind(this);
        this.handleDrag = this.handleDrag.bind(this);
        this.getLabelTransform = this.getLabelTransform.bind(this);
        this.getPinLabel = this.getPinLabel.bind(this);
        this.getViewBox = this.getViewBox.bind(this);
    }


    componentDidMount() {
        if (this.props.board) {
            this.setBoard();
        } else if (this.props.svg) {
            this.setState({
                svg: this.props.svg,
            }, this.getViewBox);
        }
    }

    componentDidUpdate(prevProps) {
        if (prevProps.board !== this.props.board) {
            this.setBoard();
        }
        if ((this.props.svg && this.props.svg !== this.state.svg)
            || (this.props.pins && this.props.pins !== this.state.pins)
        ) {
            this.setState({
                pins: this.props.pins,
                svg: this.props.svg,
            });
        }
        if (this.props.highlightedPins
            && this.props.highlightedPins.length !== prevProps.highlightedPins.length) {
            const { pins } = this.state;
            const highlightedPinsMap = {};
            for (let i = 0; i < this.props.highlightedPins.length; i += 1) {
                highlightedPinsMap[this.props.highlightedPins[i].num] = true;
            }
            for (let i = 0; i < pins.length; i += 1) {
                pins[i].highlighted = highlightedPinsMap[pins[i].num] || false;
            }
            this.setState({
                pins,
            });
        }
    }

    setBoard() {
        const device = PlatformUtils.getDeviceDefinition(this.props.board);
        if (!device) {
            return;
        }
        const pinsMap = new Map();
        if (device.pins) {
            for (let i = 0; i < device.pins.length; i += 1) {
                pinsMap.set(device.pins[i].num, device.pins[i]);
            }
        }
        if (device && device.diagram) {
            fetch(`${device.diagram}`)
                .then(res => res.text())
                .then((svg) => {
                    const pins = PlatformUtils.parseSvgPins(svg);
                    for (let i = 0; i < pins.length; i += 1) {
                        if (pinsMap.has(pins[i].num)) {
                            const pinDef = pinsMap.get(pins[i].num);
                            pins[i] = {
                                ...pinDef,
                                ...pins[i],
                            };
                            delete pins[i].id;
                        }
                    }
                    this.setState({
                        svg,
                        pins,
                    }, this.getViewBox);
                });
        }
    }

    getViewBox() {
        const { svg, pins } = this.state;
        if (!boardRef || !boardRef.current) {
            return;
        }

        let maxPinLabelWidth = 0;

        for (let i = 0; i < pins.length; i += 1) {
            const labelWidth = this.getPinLabel(pins[i]).length * 8;
            if (labelWidth > maxPinLabelWidth) {
                maxPinLabelWidth = labelWidth;
            }
        }

        const viewBox = getSvgViewbox(boardRef.current, maxPinLabelWidth * 2);

        this.setState({
            viewBox,
        });
    }

    getLabelTransform(pin) {
        if (pin.num === '') {
            return 'translate(-10000,0)';
        }
        const labelWidth = this.getPinLabel(pin).length * 8;
        let x = pin.x - labelWidth;
        let y = pin.y;
        let rotation = 0;
        let curPinX = x;
        let curPinY = y;

        // determine if the label should be on the left or right side of the pin
        // get board width and height from svg
        if (this.state.svg) {
            const parser = new DOMParser();
            const doc = parser.parseFromString(this.state.svg, 'image/svg+xml');
            const board = doc.getElementsByTagName('svg')[0];
            const boardWidth = Number(board.getAttribute('width'));
            const boardHeight = Number(board.getAttribute('height'));
            const boardX = Number(board.getAttribute('x'));
            const boardY = Number(board.getAttribute('y'));
            // determine if there are pins to the left or right of the current pin
            const pins = doc.getElementsByClassName('pin');
            const pinsArray = [];
            for (let i = 0; i < pins.length; i++) {
                pinsArray.push({
                    x: Number(pins[i].getAttribute('x')),
                    y: Number(pins[i].getAttribute('y')),
                    num: pins[i].getAttribute('data-pin'),
                });
            }
            for (let i = 0; i < pinsArray.length; i++) {
                const pinNum = pinsArray[i].num;
                if (pinNum === pin.num) {
                    curPinX = pinsArray[i].x;
                    curPinY = pinsArray[i].y;
                    break;
                }
            }

            if (curPinX > boardWidth / 2) {
                x = curPinX + 20;
            }
            let topOccupied = false;
            let bottomOccupied = false;
            let leftOccupied = false;
            let rightOccupied = false;
            let clusterX = curPinX;
            let clusterY = curPinY;
            let minDistance = boardWidth;
            const cluster = [];
            for (let i = 0; i < pinsArray.length; i += 1) {
                // calculate horizontal distance between pins
                const pin1 = pinsArray[i];
                const pinX = Number(pin1.x);
                const pinY = Number(pin1.y);
                const distanceX = Math.abs(pinX - curPinX);
                const distanceY = Math.abs(pinY - curPinY);
                const pinNum = pin1.num;
                if (pinNum === pin.num) {
                    cluster.push(pin1);
                    continue;
                }
                // check top down left right
                // check if there is a pin above the current pin
                if (distanceY > 0 && distanceY < 30
                    && curPinY > pinY
                    && distanceX === 0) {
                    topOccupied = true;
                }
                // check if there is a pin below the current pin
                if (distanceY > 0 && distanceY < 30
                    && curPinY < pinY
                    && distanceX === 0) {
                    bottomOccupied = true;
                }
                // check if there is a pin to the left of the current pin
                if (distanceX > 0 && distanceX < 30
                    && curPinX > pinX
                    && distanceY === 0) {
                    leftOccupied = true;
                }
                // check if there is a pin to the right of the current pin
                if (distanceX > 0 && distanceX < 30
                    && curPinX < pinX
                    && distanceY === 0) {
                    rightOccupied = true;
                }
                // calculate distance
                const distance = Math.sqrt((distanceX ** 2) + (distanceY ** 2));
                if (distance < minDistance) {
                    minDistance = distance + 1;
                }
            }
            for (let i = 0; i < cluster.length; i += 1) {
                const pin1 = cluster[i];
                for (let j = 0; j < pinsArray.length; j += 1) {
                    const pin2 = pinsArray[j];
                    const dataPin1 = pin1.num;
                    const dataPin2 = pin2.num;
                    if (dataPin1 === '' || dataPin2 === '' || dataPin1 === dataPin2) {
                        continue;
                    }
                    const curDistance = Math.sqrt((Math.abs(Number(pin1.x) - Number(pin2.x)) ** 2)
                        + (Math.abs(Number(pin1.y) - Number(pin2.y)) ** 2));
                    if (curDistance <= minDistance && curDistance > 0) {
                        // see if already in cluster
                        let found = false;
                        for (let k = 0; k < cluster.length; k += 1) {
                            if (cluster[k].num === dataPin2) {
                                found = true;
                                break;
                            }
                        }
                        if (found) {
                            continue;
                        }
                        cluster.push(pin2);
                        i = 0;
                        break;
                    }
                }
            }
            // get center of cluster
            for (let i = 0; i < cluster.length; i += 1) {
                clusterX += Number(cluster[i].x);
                clusterY += Number(cluster[i].y);
            }
            clusterX /= cluster.length + 1;
            clusterY /= cluster.length + 1;

            let edge = 'left';
            minDistance = Math.abs(clusterX - boardX);
            const boardRight = boardX + boardWidth;
            const boardBottom = boardY + boardHeight;
            if (Math.abs(clusterX - boardRight) < minDistance) {
                edge = 'right';
                minDistance = Math.abs(clusterX - boardRight);
            }
            if (Math.abs(clusterY - boardY) < minDistance) {
                edge = 'top';
                minDistance = Math.abs(clusterY - boardY);
            }
            if (Math.abs(clusterY - boardBottom) < minDistance) {
                edge = 'bottom';
            }
            // determine position of label
            switch (edge) {
                case 'left':
                    x = curPinX - labelWidth;
                    y = curPinY;
                    rotation = 0;
                    if (leftOccupied) {
                        x = curPinX + 20;
                    }
                    break;
                case 'right':
                    x = curPinX + 20;
                    y = curPinY;
                    rotation = 0;
                    if (rightOccupied) {
                        x = curPinX - labelWidth - 20;
                    }
                    break;
                // case 'top':
                //     x = curPinX;
                //     y = curPinY - 20;
                //     rotation = -90;
                //     if (topOccupied) {
                //         y = curPinY + 20 + labelWidth;
                //     }
                //     break;
                // case 'bottom':
                //     x = curPinX;
                //     y = curPinY + 20;
                //     rotation = 90;
                //     if (bottomOccupied) {
                //         y = curPinY - 20 - labelWidth;
                //     }
                //     break;
                default:
                    break;
            }
            if (leftOccupied && rightOccupied) {
                // set to bottom
                x = curPinX;
                y = curPinY;
                rotation = -90;
                if (topOccupied) {
                    x = curPinX + 20;
                    y = curPinY + 20;
                    rotation = 90;
                }
            }
        }

        return `translate(${x},${y}) rotate(${rotation})`;
    }

    getPinLabel(pin) {
        if (!this.props.project) {
            return '';
        }
        let label = '';
        let feature;
        for (let i = 0; i < this.props.project.features.length; i += 1) {
            const channel = this.props.project.features[i];
            if (channel.featureType !== FeatureType.PERIPHERAL) {
                continue;
            }
            for (let j = 0; j < channel.settings.length; j += 1) {
                if (channel.settings[j].settingType === SettingType.PIN
                    && channel.settings[j].value === pin.num) {
                    if (!channel.getEnabled()) {
                        continue;
                    }
                    label = `${channel.getName()}-${channel.settings[j].id} `;
                    feature = channel;
                    break;
                }
            }
            if (feature) {
                break;
            }
        }
        if (feature && feature.id === 'gpio') {
            for (let i = 0; i < this.props.project.features.length; i += 1) {
                const channel = this.props.project.features[i];
                for (let j = 0; j < channel.settings.length; j += 1) {
                    if (channel.settings[j].refId === 'gpio'
                        && channel.settings[j].value === feature.getID()) {
                        label = `${channel.getName()}-${channel.settings[j].id} `;
                        break;
                    }
                }
            }
        }

        return label;
    }

    pinMatchSearch(pin) {
        if (this.state.search.trim().length === 0) {
            return false;
        }
        return pin.function.toLowerCase().indexOf(this.state.search.toLowerCase()) >= 0;
    }

    highlightPin(event, pin) {
        if (event === undefined) {
            this.setState({
                hoverPin: pin,
            });
            return;
        }
        const offset0 = boardRef.current.getBoundingClientRect();
        const offset = event.target.getBoundingClientRect();
        let hoverPinText = `${pin.function} ${pin.description}`;
        if (this.props.hoverPin !== undefined) {
            hoverPinText = this.props.hoverPin(pin);
        }
        this.setState({
            tooltipX: Number(event.target.getAttribute('x')) + 20,
            tooltipY: Number(event.target.getAttribute('y')),
            hoverPin: pin,
            hoverPinText,
        });
    }

    handleDrag(event, data, pin) {
        if (!this.props.updatePinPosition) {
            return;
        }
        this.props.updatePinPosition(pin, data);
    }

    handleDragStop(event, data, pin) {
        const { deltaX, deltaY } = data;
    }

    selectPin(pin) {
        this.props.selectPin(pin);
    }

    clearHighlight() {
        this.setState({
            hoverPinText: '',
            hoverPin: undefined,
        });
    }

    isActive(pin) {
        const comparedProperty = pin.id !== undefined ? 'id' : 'num';
        for (let i = 0; i < this.props.selectedPins?.length; i += 1) {
            if (pin[comparedProperty] === this.props.selectedPins[i][comparedProperty]) {
                return true;
            }
        }
        return false;
    }

    renderAssignment() {
        if (this.state.hoverPin) {
            const pin = this.state.hoverPin;
            const index = this.props.selectedPins.indexOf(pin);
        }
    }

    renderFunctions() {
        return null;
    }

    renderBoard() {
        if (this.props.svg) {
            return (
                <svg
                    dangerouslySetInnerHTML={{ __html: this.props.svg }}
                />
            );
        }
        if (this.state.svg) {
            return (
                <svg
                    dangerouslySetInnerHTML={{ __html: this.state.svg }}
                />
            );
        }
    }

    render() {
        const { highlightedPins } = this.props;
        let pins = [];
        if (this.state.pins) {
            pins = this.state.pins;
        }
        const device = PlatformUtils.getDeviceDefinition(this.props.board);

        if (this.state.svg) {
            return (

                <svg
                    id="boardContainer"
                    // style={{
                    //     display: 'block', margin: 'auto',
                    // }}
                    // style={{
                    //     display: 'block', margin: 'auto', width: svgWidth, height: svgHeight,
                    // }}
                    ref={boardRef}
                    fill="none"
                    viewBox={this.state.viewBox || '-120 -50 520 845'}
                >
                    {this.renderBoard()}
                    {this.showPins ? pins.map((pin, index) => (
                        <DraggableCore
                            key={index}
                            axis="both"
                            onDrag={(e, data) => {
                                this.handleDrag(e, data, pin);
                            }}
                            // grid={[1, 1]}
                            onStop={(e, data) => {
                                // this.handleDrag(e, data, pin);
                            }}
                        >
                            <g>
                                {this.getPinLabel(pin) !== '' && (
                                    <g
                                        transform={this.getLabelTransform(pin)}
                                        onClick={() => this.selectPin(pin)}
                                        style={{
                                            cursor: 'pointer',
                                        }}
                                    >
                                        <rect
                                            fill={(this.state.hoverPin === undefined || this.state.hoverPin.num === pin.num) ? '#212529' : '#ccc'}
                                            rx={5}
                                            width={this.getPinLabel(pin).length * 8}
                                            height={16}
                                        />
                                        <rect
                                            fill={(this.state.hoverPin === undefined || this.state.hoverPin.num === pin.num) ? '#0d6efd' : '#ccc'}
                                            rx={5}
                                            width={(this.getPinLabel(pin).split('-')[0].length + 1) * 8}
                                            height={16}
                                        />
                                        <text
                                            fontFamily="monospace"
                                            fontSize="12"
                                            fill="#fff"
                                            x={5}
                                            y={12}
                                        >
                                            {this.getPinLabel(pin)}
                                        </text>
                                    </g>
                                )}
                                <Pin
                                    x={pin.x}
                                    y={pin.y}
                                    width={18}
                                    height={18}
                                    onMouseOver={event => this.highlightPin(event, pin)}
                                    onMouseOut={this.clearHighlight}
                                    onClick={() => this.selectPin(pin)}
                                    id={pin.id}
                                    pin={pin.num}
                                    label={pin.label || pin.num}
                                    highlighted={pin.highlighted ? 'true' : 'false'}
                                    isactive={this.isActive(pin) ? 'true' : 'false'}
                                />
                            </g>
                        </DraggableCore>
                    )) : null}
                    {this.props.showDefinitions ? this.renderFunctions() : null}
                    {this.state.hoverPin && this.state.hoverPinText !== '' && !this.state.hoverPin.selected && (
                        <g>
                            <rect
                                x={this.state.tooltipX}
                                y={this.state.tooltipY}
                                fill="#fff"
                                width={200}
                                height={30}
                            />
                            <text
                                fontFamily="monospace"
                                fontSize="12"
                                fill="#666"
                                x={this.state.tooltipX + 5}
                                y={this.state.tooltipY + 10}
                            >
                                {this.state.hoverPinText}
                            </text>
                        </g>
                    )}
                </svg>

            );
        }

        return (
            <span className="placeholder col-12 bg-dark" />
        );
    }
}
